import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar, Music, Video, Clock } from 'lucide-react';
import { DisplaySchedule } from '@/types/display';
import { HolidayValue } from '@/constants/holidays';
import { useHolidayYear, useHolidaySongs } from '@/hooks/useHolidayData';

interface DisplaySong {
  id: string;
  title: string;
  artist: string;
  duration?: string;
  year_introduced: number;
  video_url?: string;
  album_cover_url?: string;
}

interface HolidayContentViewProps {
  displayId: number;
  holiday: HolidayValue;
  year?: number;
}

/**
 * HolidayContentView - Read-only display of holiday-specific content
 *
 * Shows visitors:
 * - Description for this holiday
 * - Photo gallery for this holiday
 * - Videos (sequence recordings)
 * - Songs used in this holiday display
 * - Schedule for when this holiday display is active
 */
export const HolidayContentView: React.FC<HolidayContentViewProps> = ({
  displayId,
  holiday,
  year = new Date().getFullYear()
}) => {
  // Use React Query hooks for cached data fetching
  const { data: holidayYear, isLoading: isLoadingYear } = useHolidayYear(displayId, holiday, year);
  const { data: songs = [], isLoading: isLoadingSongs } = useHolidaySongs(holidayYear?.id || null);

  const isLoading = isLoadingYear || isLoadingSongs;

  // Extract data from holidayYear
  const description = holidayYear?.description || '';
  const photos = (holidayYear?.photos as string[]) || [];
  const videos = (holidayYear?.videos as string[]) || [];
  const schedule = (holidayYear?.schedule as DisplaySchedule) || null;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-2 border-current border-t-transparent rounded-full mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Loading {holiday} display...</p>
        </div>
      </div>
    );
  }

  const hasContent = description || photos.length > 0 || videos.length > 0 || songs.length > 0 || schedule;

  if (!hasContent) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p>No content available for {holiday} yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Description */}
      {description && (
        <Card>
          <CardHeader>
            <CardTitle>About This {holiday} Display</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground whitespace-pre-wrap">{description}</p>
          </CardContent>
        </Card>
      )}

      {/* Photo Gallery */}
      {photos.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Photo Gallery ({photos.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {photos.map((photo, index) => (
                <div key={index} className="relative aspect-video rounded-lg overflow-hidden bg-muted">
                  <img
                    src={photo}
                    alt={`${holiday} display photo ${index + 1}`}
                    className="w-full h-full object-cover hover:scale-105 transition-transform cursor-pointer"
                    onClick={() => window.open(photo, '_blank')}
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Videos */}
      {videos.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>
              <Video className="inline-block w-4 h-4 mr-2" />
              Sequence Videos ({videos.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {videos.map((video, index) => (
              <div key={index} className="flex items-center gap-2 p-3 bg-muted rounded-lg hover:bg-muted/80 transition-colors">
                <Video className="w-4 h-4 text-muted-foreground shrink-0" />
                <a
                  href={video}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm truncate flex-1 hover:underline text-primary"
                >
                  {video}
                </a>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Schedule */}
      {schedule && (
        <Card>
          <CardHeader>
            <CardTitle>
              <Calendar className="inline-block w-4 h-4 mr-2" />
              When to Visit
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              {schedule.startDate && schedule.endDate && (
                <div className="flex items-start gap-2">
                  <Calendar className="w-4 h-4 mt-0.5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">Dates</p>
                    <p className="text-muted-foreground">
                      {new Date(schedule.startDate).toLocaleDateString()} - {new Date(schedule.endDate).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              )}
              {schedule.hours && (
                <div className="flex items-start gap-2">
                  <Clock className="w-4 h-4 mt-0.5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">Hours</p>
                    <p className="text-muted-foreground">{schedule.hours}</p>
                  </div>
                </div>
              )}
              {schedule.notes && (
                <div className="mt-2 p-2 bg-muted/50 rounded">
                  <p className="text-muted-foreground text-xs">{schedule.notes}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Songs */}
      {songs.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>
              <Music className="inline-block w-4 h-4 mr-2" />
              Songs ({songs.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {songs.map((song, index) => (
                <div key={song.id} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors">
                  {/* Album Art */}
                  <div className="flex-shrink-0 w-12 h-12 bg-muted rounded flex items-center justify-center overflow-hidden">
                    {song.album_cover_url ? (
                      <img
                        src={song.album_cover_url}
                        alt={`${song.title} album cover`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Music className="h-6 w-6 text-muted-foreground" />
                    )}
                  </div>

                  {/* Song Info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{song.title}</p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span>{song.artist}</span>
                      {song.duration && (
                        <>
                          <span>•</span>
                          <span>{song.duration}</span>
                        </>
                      )}
                      {song.year_introduced && (
                        <>
                          <span>•</span>
                          <Badge variant="outline" className="text-xs">
                            {song.year_introduced}
                          </Badge>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Video Link */}
                  {song.video_url && (
                    <a
                      href={song.video_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="shrink-0"
                    >
                      <Badge variant="secondary" className="gap-1">
                        <Video className="w-3 h-3" />
                        Watch
                      </Badge>
                    </a>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default HolidayContentView;
